import ImpressionHandler from '@services/ImpressionHandler';
import { isEmpty } from '@utilities/collection-utils';

declare global {
  interface Window {
    dataLayer?: any[];
  }
}

const euroRegExp = /(euro|uefaeuro)(-*)(\d+)/;

export default class GoogleTagManagerHelper {
  /**
   * Push somethig in dataLayer
   * @param obj object to push
   */
  static _push(obj: any) {
    if (!obj) return;

    window.dataLayer = window.dataLayer ?? [];

    window.dataLayer.push(obj);
  }

  /**
   * Get data from dataLayer
   * @param key the key to search
   */
  static getFromDataLayer(key: string) {
    if (!window.dataLayer?.length) return '';

    const dataLayerBlock = window.dataLayer.findLast((el) => el.hasOwnProperty(key));
    if (!dataLayerBlock) return '';

    return dataLayerBlock[key];
  }

  private static convertToGa4Sponsor = (data: Record<string, any>) => ({
    promotion_id: data.id,
    promotion_name: data.name,
    creative_name: data.creative,
    creative_slot: data.position,
  });

  /**
   * Push Promotion Views
   * @param promotions list of impressions object
   */
  static gtmSponsorImpression(promotions: Record<string, any>[]) {
    const items = promotions.map((product) => GoogleTagManagerHelper.convertToGa4Sponsor(product));
    GoogleTagManagerHelper.ga4ListEvent('Promotion Impression', 'view_promotion', items);
  }

  /**
   * Push Promotion Click
   * @param promo the promo object
   */
  static gtmSponsorClick(_el: HTMLElement, promo: Record<string, any>) {
    ImpressionHandler.getInstance('sponsors')?.send();
    const item = GoogleTagManagerHelper.convertToGa4Sponsor(promo);
    GoogleTagManagerHelper.ga4ListEvent('Promotion Click', 'select_promotion', [item]);
  }

  private static convertToGa4Card = (data: Record<string, any>) => ({
    ...Object.fromEntries(
      Object.keys(data).map((key) => [key.startsWith('item_') || key === 'index' ? key : `item_${key}`, data[key]]),
    ),
  });

  /**
   * Push Card Impressions
   * @param items list of items object
   * @param eventLabel event label
   */
  static gtmCardImpression(items: any[]) {
    GoogleTagManagerHelper.ga4ListEvent('Card Impression', 'view_item_list', items);
  }

  static getCardTrackingData(el: HTMLElement, data: Record<string, any>) {
    let containerPosition: number;
    let cardPosition: number;
    let container: string;
    const parent = el.closest<HTMLElement>('.js-tracking-container');
    if (parent) {
      if (!parent.dataset.position) {
        parent.dataset.position = [...document.querySelectorAll('.js-tracking-container')]
          .findIndex((el) => el === parent)
          .toString();
      }

      container = parent.dataset.trackingContainer;
      containerPosition = +parent.dataset.position + 1;
      cardPosition =
        [...parent.querySelectorAll('.js-tracking-card,[data-tracking-type="card"]')].findIndex(
          (child) => child === el || child.contains(el),
        ) + 1;
    }

    const list = [
      GoogleTagManagerHelper.getFromDataLayer('pageType'),
      GoogleTagManagerHelper.getFromDataLayer('pageSubType'),
      container,
      containerPosition,
    ]
      .filter(Boolean)
      .join('-');

    data = GoogleTagManagerHelper.convertToGa4Card(data);

    data.item_brand ??= GoogleTagManagerHelper.getFromDataLayer('competition');
    data.item_list_name = list || undefined;
    data.index ||= cardPosition;

    return data;
  }

  static gtmCardElementClick(el: HTMLElement, data: Record<string, any>) {
    ImpressionHandler.getInstance('cards')?.send();

    const item = GoogleTagManagerHelper.getCardTrackingData(el, data);
    const event = el.dataset?.event || 'Card';
    GoogleTagManagerHelper.ga4ListEvent(`${event} Click`, 'select_item', [item]);
  }

  /**
   * Push List Event
   * @param event Event name
   * @param event_name GA4 vent name
   * @param items list of items object
   */
  static ga4ListEvent(event: string, event_name: string, items: Record<string, any>[]) {
    GoogleTagManagerHelper._push({
      event,
      event_name,
      ecommerce: {
        items,
      },
    });
  }

  /**
   * Push an Event
   */
  static event(
    category: string,
    action: string,
    label = '',
    data: Record<string, any> = {},
    name = '',
    nonInteraction = false,
    eventType = 'gaEvent',
  ) {
    if (name) {
      return GoogleTagManagerHelper.ga4Event(name, {
        event_category: category ? category : undefined,
        event_action: action ? action : undefined,
        event_label: label ? label : undefined,
        ...data,
      });
    }

    GoogleTagManagerHelper.legacyEvent(category, action, label, data, nonInteraction, eventType);
  }
  /**
   * Push a GA4 Event
   */
  static ga4Event(name: string, data: Record<string, any>) {
    GoogleTagManagerHelper._push({ event: 'ga4Event', event_name: name, [name]: data ?? {} });
  }

  /**
   * Push a legacy GA Event
   */
  private static legacyEvent(
    category: string,
    action: string,
    label = '',
    data: Record<string, any>,
    nonInteraction = false,
    eventType = 'gaEvent',
  ) {
    GoogleTagManagerHelper._push({
      event: eventType,
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      non_interaction: nonInteraction,
      ...(data ?? {}),
    });
  }

  /**
   * Puash a share event
   */
  static share(social: string, title: string) {
    GoogleTagManagerHelper.event('Social Media Activity', `${social} Share`, title);
  }

  /**
   * Push new Page Meta
   * @param data the meta data
   */
  static addPageMeta(data: any) {
    if (isEmpty(data) || window.thirdParties) return;

    /**
     * the IFs are needed because we don't want to send unwanted undefined values
     * because they would oveeride the existing values in the dataLayer
     */
    if (data.pageName) {
      data.pageName = data.pageName.replace(euroRegExp, 'uefaeuro$3');
    }
    if (data.virtPath) {
      data.virtPath = data.virtPath.replace(euroRegExp, 'uefaeuro$3');
    }

    GoogleTagManagerHelper._push({
      event: 'Page Meta',
      ...data,
    });
  }

  static addVirtualPage(data: any, nonInteraction = true) {
    if (isEmpty(data)) return;

    /**
     * the IFs are needed because we don't want to send unwanted undefined values
     * because they would oveeride the existing values in the dataLayer
     */
    if (data.pageName) {
      data.pageName = data.pageName.replace(euroRegExp, 'uefaeuro$3');
    }
    if (data.virtPath) {
      data.virtPath = data.virtPath.replace(euroRegExp, 'uefaeuro$3');
    }

    GoogleTagManagerHelper._push({
      event: 'virtPath',
      non_interaction: nonInteraction,
      ...data,
    });
  }

  /**
   * Push Link click
   */
  static gtmLinkClick(el: HTMLElement, data: any, nonInteraction = false) {
    if (isEmpty(data)) return;

    GoogleTagManagerHelper.event(
      data.category,
      data.action || (el as HTMLLinkElement).href,
      data.label,
      data.more,
      data.name,
      nonInteraction,
    );
  }

  /**
   * Push CTA click
   */
  static gtmCtaClick(cta: HTMLElement, nonInteraction = false) {
    const list = `${GoogleTagManagerHelper.getFromDataLayer('pageType')}-${GoogleTagManagerHelper.getFromDataLayer('pageSubType')}`;
    const product = {
      ...(JSON.parse(cta.dataset.tracking) as Record<string, any>),
      brand: GoogleTagManagerHelper.getFromDataLayer('competition'),
    };
    GoogleTagManagerHelper._push({
      event: 'CTA Click',
      non_interaction: nonInteraction,
      ecommerce: {
        click: {
          actionField: { list },
          products: [product],
        },
      },
    });
  }
}
